import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseFectch';
import {
  leaveResponsetype,
  SubmitLeaveApplicationPayload,
  uninformedleaveType,
} from '../../types/request';

export const requestApi = createApi({
  reducerPath: 'requestApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'requestForLeave',
    'getAllRequests',
    'getAllEmployeeRequests',
    'uninformedLeaveRequest',
    'LeaveData',
    'ProfileEditRequests',
  ],
  endpoints: (builder) => ({
    leaveRequest: builder.mutation<any, any>({
      query: (leaveData) => ({
        url: '/leave',
        method: 'POST',
        body: leaveData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [
        'getAllRequests',
        'getAllEmployeeRequests',
        'uninformedLeaveRequest',
      ],
    }),

    uninformedLeaveRequest: builder.mutation<
      leaveResponsetype,
      uninformedleaveType
    >({
      query: (leaveData) => ({
        url: '/uninformedLeave',
        method: 'POST',
        body: leaveData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [
        'getAllRequests',
        'getAllEmployeeRequests',
        'requestForLeave',
      ],
    }),

    getAllRequest: builder.query<any, { _id: string | undefined }>({
      query: ({ _id }) => ({ url: `/getRequests/${_id}` }),
      providesTags: ['getAllRequests'],
    }),

    getEmployeeLeaveRequest: builder.query<any, { _id: string | undefined }>({
      query: ({ _id }) => ({ url: `/my-requests/${_id}` }),
      providesTags: ['getAllEmployeeRequests'],
    }),

    resolveRequest: builder.mutation<
      any,
      { id?: string; userId?: string; status?: string; msg?: string }
    >({
      query: ({ id, userId, status, msg }) => ({
        url: '/resolve',
        method: 'POST',
        body: { id, userId, status, msg },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests'],
    }),

    getSchedule: builder.query<any, { id: string; companyId: string }>({
      query: ({ id, companyId }) => `/schedule/${id}?companyId=${companyId}`,
    }),

    resolveSchedule: builder.mutation<
      any,
      { id?: string; userId?: string; status?: string; msg?: string }
    >({
      query: ({ id, userId, status, msg }) => ({
        url: '/resolve',
        method: 'POST',
        body: { id, userId, status, msg },
        credentials: 'include',
      }),
      invalidatesTags: ['getAllRequests'],
    }),

    leaveAdd: builder.mutation<any, Record<string, any>>({
      query: (body) => ({
        url: '/hr/apply',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),

    deleteRequest: builder.mutation<any, { _id: string | null }>({
      query: ({ _id }) => ({
        url: `/leaves/delete/${_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),

    getStatusData: builder.query<
      any,
      { companyId: string; startDate?: string; endDate?: string }
    >({
      query: ({ companyId, startDate, endDate }) =>
        `/status?companyId=${companyId}${
          startDate ? `&startDate=${startDate}` : ''
        }${endDate ? `&endDate=${endDate}` : ''}`,
      providesTags: ['LeaveData'],
    }),

    getStatusById: builder.query<any, string>({
      query: (statusId) => `status/${statusId}`,
    }),

    submitLeaveApplication: builder.mutation<
      any,
      SubmitLeaveApplicationPayload
    >({
      query: (formData) => ({
        url: '/slack-leave',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: formData,
      }),
      invalidatesTags: ['LeaveData'],
    }),

    updateAvailability: builder.mutation<
      any,
      { slack_id: string; _id: string }
    >({
      query: ({ slack_id, _id }) => ({
        url: `/slack/notify/${slack_id}`,
        method: 'POST',
        body: { id: _id },
      }),
      invalidatesTags: ['getAllRequests', 'getAllEmployeeRequests'],
    }),

    getGrievancesByCompany: builder.query<any, string>({
      query: (companyId) => `/grievances/company/${companyId}`,
    }),

    getGrievancesByEmployee: builder.query<any, string>({
      query: (employeeId) => `/grievances/employee/${employeeId}`,
    }),

    createGrievance: builder.mutation<any, any>({
      query: (data) => ({
        url: '/grievances',
        method: 'POST',
        body: data,
      }),
    }),

    updateGrievanceStatus: builder.mutation<
      any,
      { grievanceId: string; status: string }
    >({
      query: ({ grievanceId, status }) => ({
        url: `/grievances/status/${grievanceId}`,
        method: 'PATCH',
        body: { status },
      }),
    }),

    getProductivityData: builder.query<any, string>({
      query: (companyId) => `productivity?companyId=${companyId}`,
    }),

    getEmployeeLeaveBalance: builder.query<any, string>({
      query: (employeeId) => `leave-balance/${employeeId}`,
    }),

    getCompanyScreenshots: builder.query<any, string>({
      query: (companyId) => `/images/${companyId}`,
    }),

    getEmployeesLeave: builder.query<any, string>({
      query: (companyId) => `/company/${companyId}/employees-leave`,
    }),

    updateLeave: builder.mutation<any, { id: string; leaveData: any }>({
      query: ({ id, leaveData }) => ({
        url: `/leave/update/${id}`,
        method: 'PUT',
        body: leaveData,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: [
        'getAllRequests',
        'getAllEmployeeRequests',
        'LeaveData',
      ],
    }),

    createProfileEditRequest: builder.mutation<any, { requestedChanges: any }>({
      query: (body) => ({
        url: '/profile-edit',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ProfileEditRequests'],
    }),

    getCompanyProfileEditRequests: builder.query<any, string>({
      query: (companyId) => ({ url: `/profile-edit/company/${companyId}` }),
      providesTags: ['ProfileEditRequests'],
    }),

    getMyProfileEditRequests: builder.query<any, string>({
      query: (employeeId) => ({
        url: `/profile-edit/my-requests/${employeeId}`,
      }),
      providesTags: ['ProfileEditRequests'],
    }),

    getProfileEditRequestById: builder.query<any, string>({
      query: (requestId) => ({ url: `/profile-edit/${requestId}` }),
    }),

    approveProfileEditRequest: builder.mutation<any, any>({
      query: ({ requestId, reviewedBy }) => ({
        url: `/profile-edit/approve/${requestId}`,
        method: 'PATCH',
        body: { reviewedBy },
      }),
      invalidatesTags: ['ProfileEditRequests'],
    }),

    rejectProfileEditRequest: builder.mutation<
      any,
      { requestId: string; rejectionReason: string; reviewedBy: string }
    >({
      query: ({ requestId, rejectionReason, reviewedBy }) => ({
        url: `/profile-edit/reject/${requestId}`,
        method: 'PATCH',
        body: { rejectionReason, reviewedBy },
      }),
      invalidatesTags: ['ProfileEditRequests'],
    }),

    deleteProfileEditRequest: builder.mutation<any, string>({
      query: (requestId) => ({
        url: `/profile-edit/${requestId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProfileEditRequests'],
    }),
  }),
});

export const {
  useLeaveRequestMutation,

  useResolveRequestMutation,
  useLazyGetScheduleQuery,
  useLazyGetEmployeeLeaveBalanceQuery,
  useLazyGetEmployeesLeaveQuery,
  useLazyGetGrievancesByCompanyQuery,
  useCreateGrievanceMutation,
  useUpdateGrievanceStatusMutation,
  useLazyGetCompanyScreenshotsQuery,
  useLazyGetProductivityDataQuery,
  useUpdateLeaveMutation,
  useCreateProfileEditRequestMutation,
  useLazyGetCompanyProfileEditRequestsQuery,
  useApproveProfileEditRequestMutation,
  useRejectProfileEditRequestMutation,
  useDeleteProfileEditRequestMutation,
  useLazyGetProfileEditRequestByIdQuery,
  useLazyGetGrievancesByEmployeeQuery,
  useLazyGetMyProfileEditRequestsQuery,
  useLazyGetEmployeeLeaveRequestQuery
} = requestApi;
